/**
 * rte-sanitizer.js
 *
 * Cleans leftover Word/Google Docs paste junk out of rich-text areas (`.rte`)
 * at render time: product descriptions, page content, popups, accordions,
 * size charts, etc. This is a safety net for content that hasn't been run
 * through the offline CSV cleanup yet (or gets hand-edited later) — it does
 * NOT replace cleaning the underlying data, it just stops junk that slips
 * through from rendering on the storefront.
 *
 * Runs only on the (small) rich-text containers that exist on the current
 * page, once each, on real DOM nodes already present or newly inserted
 * (quick view, AJAX cart, infinite scroll, etc.) — not a full-page/catalog
 * walk, so the cost is negligible.
 *
 * Prevention mechanism: any element (and everything inside it) carrying the
 * class "no-cleaning" is left completely untouched, tags/attributes/style
 * and all. Add that class to a wrapper in the rich-text HTML source to
 * protect a specific block from cleanup.
 */
(function () {
  var SKIP_CLASS = 'no-cleaning';
  var CONTAINER_SELECTOR = '.hdt-product__description .rte';

  var ALLOWED_TAGS = new Set([
    'A', 'B', 'STRONG', 'EM', 'I', 'P', 'DIV',
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
    'UL', 'OL', 'LI', 'BR', 'SPAN', 'BLOCKQUOTE', 'IMG'
  ]);

  // Tags removed entirely (tag + content) rather than unwrapped.
  var DROP_ENTIRELY = new Set([
    'SCRIPT', 'STYLE', 'META', 'LINK', 'NOSCRIPT',
    'IFRAME', 'OBJECT', 'EMBED', 'FORM', 'O:P', 'XML'
  ]);

  // Note: `dir` is deliberately NOT preserved here. Word/Google Docs paste
  // routinely stamps dir="ltr" (and lang="EN-US") on paragraphs regardless
  // of their actual content, which is a common cause of RTL text rendering
  // left-aligned/reversed. Correct bidi resolution comes from the page-level
  // dir="rtl" plus unicode-bidi: plaintext on .rte block children (see
  // base.css). A genuine manual override should go in a no-cleaning block.
  var ALLOWED_ATTRS = {
    A: ['href', 'target', 'rel'],
    IMG: ['src', 'alt', 'width', 'height', 'loading'],
    '*': []
  };

  function hasSkipClass(el) {
    return el.classList && el.classList.contains(SKIP_CLASS);
  }

  function stripComments(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT, null);
    var toRemove = [];
    var node;
    while ((node = walker.nextNode())) toRemove.push(node);
    toRemove.forEach(function (n) { n.remove(); });
  }

  function sanitizeChildren(root) {
    // Snapshot first: unwrapping/removing while iterating live children breaks iteration.
    var children = Array.prototype.slice.call(root.children);
    children.forEach(function (el) {
      if (hasSkipClass(el)) return; // element + its whole subtree stays exactly as-is

      var tag = el.tagName;

      if (DROP_ENTIRELY.has(tag)) {
        el.remove();
        return;
      }

      sanitizeChildren(el); // clean nested junk before deciding this node's fate

      var keep = (ALLOWED_ATTRS[tag] || []).concat(ALLOWED_ATTRS['*']);
      Array.prototype.slice.call(el.attributes).forEach(function (attr) {
        if (keep.indexOf(attr.name.toLowerCase()) === -1) el.removeAttribute(attr.name);
      });

      if (!ALLOWED_TAGS.has(tag)) {
        // Unwrap: keep the (already-cleaned) content, drop the tag itself.
        while (el.firstChild) root.insertBefore(el.firstChild, el);
        el.remove();
      }
    });
  }

  function sanitize(container) {
    if (!container || container.dataset.rteSanitized === 'true') return;
    if (hasSkipClass(container)) {
      container.dataset.rteSanitized = 'true';
      return;
    }
    stripComments(container);
    sanitizeChildren(container);
    container.dataset.rteSanitized = 'true';
  }

  function sanitizeAll(scope) {
    (scope || document).querySelectorAll(CONTAINER_SELECTOR).forEach(sanitize);
  }

  function init() {
    sanitizeAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run when the theme editor swaps a section, or content is injected later
  // (quick view, AJAX cart drawer, predictive search, infinite scroll, etc.).
  document.addEventListener('shopify:section:load', function (e) { sanitizeAll(e.target); });

  new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      m.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches(CONTAINER_SELECTOR)) sanitize(node);
        if (node.querySelectorAll) sanitizeAll(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();


/* ================================================= */
/* ================================================= */
/* ===================== BI-DI ===================== */
/* ================================================= */
/* ================================================= */



  (() => {
    const CONTENT_SELECTORS = [

/* =============== ADD SELECTORS HERE ==================== */
      '.rte',
      '.product__description',
      '.product-description',
      '.collection-description',
      '.collection__description',
      '.page-width',
      '.rich-text__text',
      '.multicolumn-card__info',
      '.accordion__content',
      '[data-product-description]',
      '.rte',
      '.hdt-product__title',
      '.collection-hero__title',
      '.hdt-card-product__title',
      '.hdt-collection-card__title',
      '.hdt-collection-list__title',
      '.hdt-predictive-search__item-heading',
      '.hdt-breadcrumb-link',
      '.hdt-heading',
      '.hdt-tab-title',
      '.hdt-dropdown__link',
      '.hdt-menu-drawer__menu-item',
      '.hdt-menu-drawer__panel-menu-item'

/* =============== NO CHANGES BELOW THIS LINE ==================== */

    ];


    const SKIP_TAGS = new Set([
      'SCRIPT',
      'STYLE',
      'NOSCRIPT',
      'TEXTAREA',
      'INPUT',
      'SELECT',
      'OPTION',
      'CODE',
      'PRE',
      'SVG',
      'BIDI',
      'BDO'
    ]);

    // Unicode isolate marks: LRI (Left-to-Right Isolate) / PDI (Pop Directional Isolate)
    const LRI = '\u2066';
    const PDI = '\u2069';

    const latinTextPattern =
      /(?:[0-9]+[\s./&+\-–—]*)?(?:[A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ0-9.'’®™%/+&\-–—]*(?:\s+(?:[&+/\-–—]\s+)?[A-Za-zÀ-ÖØ-öø-ÿ0-9][A-Za-zÀ-ÖØ-öø-ÿ0-9.'’®™%/+&\-–—]*)*)/g;

    function containsLatin(text) {
      return /[A-Za-zÀ-ÖØ-öø-ÿ]/.test(text);
    }

    function shouldSkipNode(textNode) {
      const parent = textNode.parentElement;

      if (!parent || SKIP_TAGS.has(parent.tagName)) {
        return true;
      }

      if (
        parent.closest(
          'bdi, bdo, script, style, textarea, input, select, option, code, pre, svg'
        )
      ) {
        return true;
      }

      const text = textNode.nodeValue;

      return (
        !text ||
        !text.trim() ||
        !containsLatin(text) ||
        // Already processed (contains isolate marks) — avoid double-wrapping
        text.includes(LRI)
      );
    }

    function isolateEnglishText(textNode) {
      if (shouldSkipNode(textNode)) {
        return;
      }

      const text = textNode.nodeValue;
      let result = '';
      let lastIndex = 0;
      let hasMatch = false;

      latinTextPattern.lastIndex = 0;

      for (const match of text.matchAll(latinTextPattern)) {
        const matchedText = match[0];
        const startIndex = match.index;

        if (!containsLatin(matchedText)) {
          continue;
        }

        hasMatch = true;

        result += text.slice(lastIndex, startIndex);
        result += LRI + matchedText + PDI;

        lastIndex = startIndex + matchedText.length;
      }

      if (!hasMatch) {
        return;
      }

      result += text.slice(lastIndex);

      // Plain text mutation — no new DOM nodes needed, unlike the <bdi> approach.
      textNode.nodeValue = result;
    }

    function processContainer(container) {
      if (!container || container.dataset.bidiProcessed === 'true') {
        return;
      }

      const walker = document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT
      );

      const textNodes = [];
      let currentNode;

      while ((currentNode = walker.nextNode())) {
        textNodes.push(currentNode);
      }

      textNodes.forEach(isolateEnglishText);
      container.dataset.bidiProcessed = 'true';
    }

    function processPage(root = document) {
      const containers = new Set();

      if (
        root instanceof Element &&
        root.matches(CONTENT_SELECTORS.join(','))
      ) {
        containers.add(root);
      }

      root
        .querySelectorAll?.(CONTENT_SELECTORS.join(','))
        .forEach((element) => containers.add(element));

      containers.forEach(processContainer);
    }

    function initBidiFix() {
      processPage();

      /*
       * Обрабатывает контент, который Shopify или приложения
       * добавляют на страницу динамически.
       */
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType !== Node.ELEMENT_NODE) {
              return;
            }

            processPage(node);
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initBidiFix);
    } else {
      initBidiFix();
    }

    /*
     * Повторная обработка секций в редакторе темы Shopify.
     */
    document.addEventListener('shopify:section:load', (event) => {
      event.target
        .querySelectorAll?.('[data-bidi-processed]')
        .forEach((element) => {
          delete element.dataset.bidiProcessed;
        });

      processPage(event.target);
    });
  })();